import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Eye, Layers, Share2, Smartphone } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { SEO } from '../components/SEO';

export const CreoView = () => {
  const features = [
    {
      icon: <Eye className="w-10 h-10 text-[#00890b]" />,
      title: '3D Visualization',
      description: 'High-fidelity 3D viewing without requiring CAD software',
    },
    {
      icon: <Layers className="w-10 h-10 text-[#00890b]" />,
      title: 'Multi-CAD Support',
      description: 'View and markup files from multiple CAD systems',
    },
    {
      icon: <Share2 className="w-10 h-10 text-[#00890b]" />,
      title: 'Collaboration Tools',
      description: 'Markups, annotations, and design review capabilities',
    },
    {
      icon: <Smartphone className="w-10 h-10 text-[#00890b]" />,
      title: 'Mobile Access',
      description: 'Access product data anywhere with mobile visualization',
    },
  ];

  const capabilities = [
    '3D Model Viewing',
    '2D Drawing Visualization',
    'Assembly Exploded Views',
    'Section Views & Clipping',
    'Markup & Annotations',
    'Measurement Tools',
    'Model Comparison',
    'Animation Playback',
    'BOM Visualization',
    'AR/VR Integration',
    'Publish 3D PDF',
    'Web-based Viewing',
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Hire Creo View Expert | Outsource Windchill Visualization Development"
        description="Hire expert Creo View and Windchill Visualization developers. Outsource your 3D visualization and CAD viewing solutions to experienced professionals."
        keywords="hire creo view expert, outsource windchill visualization, hire visualization developer, creo view consultant, hire windchill viewer expert"
        canonical="/hire-creo-view-visualization-expert"
      />
      <section className="bg-gradient-to-br from-white via-green-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-[#00890b] hover:bg-[#006a08] text-white px-4 py-2">
              Visualization Experts
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Hire Creo View & Windchill Visualization Expert
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Outsource your visualization development to expert consultants. Hire experienced
              Creo View specialists for enterprise-wide product visualization
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Creo View & Windchill Visualization
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Creo View and Windchill Visualization enable stakeholders across the enterprise to
            visualize, share, and collaborate on product designs without requiring CAD licenses.
            Our visualization experts help organizations maximize the value of their product data
            through customized viewing and collaboration solutions.
          </p>
          <p className="text-lg text-gray-600">
            We provide comprehensive services including Creo View implementation, custom viewer
            development, integration with Windchill, and mobile visualization solutions.
          </p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-center mb-4">{feature.icon}</div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Visualization Capabilities
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors"
              >
                <CheckCircle className="w-6 h-6 text-[#00890b] flex-shrink-0" />
                <span className="text-gray-700 font-medium">{capability}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Expert Creo View & Visualization Services
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Our Creo View and Windchill Visualization specialists deliver custom solutions that
            extend product data access across your organization. From manufacturing floor to
            executive dashboards, we enable stakeholders to interact with product information in
            meaningful ways.
          </p>
          <p className="text-lg text-gray-600">
            We help organizations implement advanced visualization scenarios including augmented
            reality (AR), virtual reality (VR), and mobile visualization for field service and
            remote collaboration.
          </p>
        </div>
      </section>

      <ContactForm />
    </div>
  );
};